/* HRM_CLEANUP: Delete this component when Ontario MD certification is complete */
import { useCallback } from "react";

import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { Button } from "@/DesignSystem";
import useApi from "../../Hooks/useApi";

interface CleanupOntarioMdDataSummary {
    DocumentsMarkedDeleted: number;
    MedicalDocumentsDeleted: number;
    DocumentBytesDeleted: number;
    DocumentPatientLinksDeleted: number;
    DocumentResponsibleProvidersDeleted: number;
    DocumentDocumentNotesDeleted: number;
    DocumentNotesDeleted: number;
    DocumentAnnotationsDeleted: number;
    OrphanDocumentsDeleted: number;
    AttachmentsArchived: number;
    NotificationsDeleted: number;
    FailuresDeleted: number;
    PullLogsDeleted: number;
    DocumentVersionsDeleted: number;
    DocumentRawMetadataDeleted: number;
    HrmSchemaRowsDeleted: number;
}

interface CleanupOntarioMdDataResponse {
    Summary?: CleanupOntarioMdDataSummary;
}

type RawCleanupOntarioMdDataResponse = CleanupOntarioMdDataResponse & {
    Errors?: ErrorMessage[];
    Warnings?: ErrorMessage[];
};

/**
 * Removes Ontario MD Health Report Manager artifacts for the clinic.
 * Returns a summary of the entities that were deleted or archived.
 */
// Uses the frontend ApiClient to call the registered operation.

function CleanupDataButton(): JSX.Element {
    const { callApi } = useApi();

    const handleClick = useCallback(async (): Promise<void> => {
        const confirmed = window.confirm(
            "Delete OntarioMD HRM data? This will permanently remove HRM documents, notifications and logs for this clinic."
        );

        if (!confirmed) return;

        await callApi(() => Client.postAsync<RawCleanupOntarioMdDataResponse>(ServerOperations.Server.Myle_OntarioMdHealthReportManagerModule_CleanupData, {}), {
            onSuccess: data => {
                const summary = (data as any)?.Summary;
                if (!summary) {
                    window.alert("OntarioMD cleanup completed.");
                    return;
                }

                const parts: string[] = [];
                const add = (label: string, value: number | undefined): number => parts.push(`${label}: ${value ?? 0}`);

                add("Documents marked deleted", summary.DocumentsMarkedDeleted);
                add("Medical documents deleted", summary.MedicalDocumentsDeleted);
                add("Document annotations deleted", summary.DocumentAnnotationsDeleted);
                add("Document bytes deleted", summary.DocumentBytesDeleted);
                add("Document-patient links deleted", summary.DocumentPatientLinksDeleted);
                add("Document responsible providers deleted", summary.DocumentResponsibleProvidersDeleted);
                add("Document document-notes deleted", summary.DocumentDocumentNotesDeleted);
                add("Document notes deleted", summary.DocumentNotesDeleted);
                add("Document raw metadata deleted", summary.DocumentRawMetadataDeleted);
                add("Orphan documents deleted", summary.OrphanDocumentsDeleted);
                add("Attachments archived", summary.AttachmentsArchived);
                add("Notifications deleted", summary.NotificationsDeleted);
                add("Failures deleted", summary.FailuresDeleted);
                add("Pull logs deleted", summary.PullLogsDeleted);
                add("Document versions deleted", summary.DocumentVersionsDeleted);
                add("HRM schema rows deleted", summary.HrmSchemaRowsDeleted);

                window.alert(`OntarioMD cleanup completed.\n\n${parts.join("\n")}`);
            },
            onError: () => window.alert("Failed to cleanup HRM data.")
        });
    }, [callApi]);

    return (
        <Button
            label="Cleanup HRM data"
            onClick={handleClick}
        />
    );
}

export { CleanupDataButton };
